import { useCallback, useEffect, useState } from 'react';

/**
 * The Travel Acknowledgment, as the traveller sees it.
 *
 * ⚠️⚠️ THIS PAGE RENDERS WITH NO ACCOUNT AND NO SIGN-IN. It is reached from a
 * link a scheduler texts or emails, by somebody who has never opened the
 * Project Tracker. Everything below follows from that:
 *
 *   • It is mounted ABOVE the MSAL templates in `App.tsx`, not inside the
 *     router. Inside `<UnauthenticatedTemplate>` a visitor gets the sign-in
 *     screen, which is the one thing this link must never show.
 *   • It uses plain `fetch`, NOT `lib/apiClient`. The shared client runs an
 *     interceptor that acquires a bearer token and, for a signed-in account
 *     whose token has expired, THROWS rather than sending the request. None of
 *     that belongs on a page whose whole premise is no account.
 *   • It borrows no class from any other module. See the note on `css` below.
 *
 * ── ⚠️ THIS FILE AUTHORS NO COPY AT ALL ─────────────────────────────────────
 *
 * Every word the reader sees — the welcome, the amounts' labels, the policy
 * for their band, the sixteen general terms, the acknowledgment statement, the
 * checkbox, the button — arrives from the API and is rendered verbatim. It is
 * the approved, translated text from the Spanish Translation Review document,
 * frozen into this acknowledgment when the link was issued.
 *
 * So the strings in this file are: "Loading…", and two error sentences for
 * cases the form has no approved wording for (a link that does not resolve, a
 * request that failed). Its spec asserts there is nothing else.
 *
 * ⚠️ THE TEXT IS FROZEN PER ROW, WHICH IS WHY IT COMES DOWN THE WIRE rather
 * than being imported. A copy module in this bundle would restate every old
 * acknowledgment in today's terms the first time the handbook is revised,
 * with somebody's signature still attached to it.
 *
 * ── Language ────────────────────────────────────────────────────────────────
 *
 * Both versions are frozen into the record, so the toggle needs no request and
 * cannot show a version that was not part of this acknowledgment. Which one
 * they signed in is recorded — it is a fact about what they were shown, not a
 * display preference — and a signed link reopens in that language.
 */

type Lang = 'en' | 'es';

/** The approved text for one language, exactly as it was frozen. */
export interface Policy {
  formTitle: string;
  welcome: string;
  benefitsHeading: string;
  /** The one tier section that applies to this band. */
  tierPolicy: string;
  generalTerms: string;
  acknowledgment: string;
  signatureHint: string;
  checkboxLabel: string;
  submitLabel: string;
  signedPrefix: string;
  labels: {
    employeeName: string;
    projectName: string;
    jobNumber: string;
    /**
     * Optional: frozen only on links issued since these fields were added, so
     * an older form has neither the label nor the value, and shows neither.
     */
    siteAddress?: string;
    regionalOffice?: string;
    mobilizationDate?: string;
    typeYourFullName: string;
  };
  amountLabels: Record<string, string>;
  unitLabels: Record<'once' | 'per-day' | 'per-hour', string>;
}

export interface Acknowledgment {
  job: {
    jobNumber: string | null;
    parentJobNumber: string | null;
    title: string | null;
    travelBand: string | null;
    /**
     * As frozen when the link was issued. Optional because a row issued before
     * the API projected it has no value, and that must read as "not
     * prevailing wage" rather than as a guess.
     */
    isPrevailingWage?: boolean;
    /** Verified site address, frozen at issue. Absent on older links. */
    siteAddress?: string | null;
    /** `YYYY-MM-DD`, frozen at issue. Absent on older links. */
    mobilizationDate?: string | null;
  };
  /** `regionalOffice` is the office as shown at issue, e.g. "Kentucky". Absent on older links. */
  person: { name: string | null; regionalOffice?: string | null };
  /** The computed amounts. Labelled from `policy`, never from these labels. */
  lines: { kind: string; label: string; rate: number; unit: 'once' | 'per-day' | 'per-hour' }[];
  /**
   * ⚠️ NULLABLE. Rows issued before the policy was frozen have none, and the
   * page must say so rather than render a form with amounts and no terms.
   */
  policy: Record<Lang, Policy> | null;
  issuedAt: string;
  signedName: string | null;
  signedAt: string | null;
  signedLanguage: string | null;
}

const API_ROOT = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api/v1';

/**
 * ⚠️ THE ONLY SENTENCES THIS FILE AUTHORS, and they exist because the approved
 * document has no wording for them: it is a Formstack form that either loads
 * or 404s at the server, so it never had to say "this link is not valid".
 *
 * Both are about the LINK, never about money. The Spanish reuses the
 * document's own register (usted) and vocabulary rather than introducing new
 * policy terms — and is the one thing on this page a translator has not seen.
 */
const FALLBACK: Record<Lang, { notFound: string; failed: string; loading: string }> = {
  en: {
    notFound: 'This link is not valid. Check that you copied all of it, or ask your '
      + 'scheduler to send it again.',
    failed: 'This form could not be loaded. Check your connection and try again.',
    loading: 'Loading…',
  },
  es: {
    notFound: 'Este enlace no es válido. Verifique que lo copió completo, o pida a su '
      + 'programador que se lo envíe de nuevo.',
    failed: 'No se pudo cargar este formulario. Verifique su conexión e intente de nuevo.',
    loading: 'Cargando…',
  },
};

/**
 * A frozen `YYYY-MM-DD` in the reader's language — "September 30, 2026" or
 * "30 de septiembre de 2026". The month names are the browser's locale data,
 * not words this file writes.
 *
 * ⚠️ FORMATTED IN UTC. The value is a calendar date, not an instant: read in
 * the reader's zone, midnight UTC is the previous evening anywhere west of
 * Greenwich, and every date on the form would show a day early.
 */
export function formatFormDate(ymd: string, lang: Lang): string {
  const d = new Date(`${ymd}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return ymd;
  return new Intl.DateTimeFormat(lang === 'es' ? 'es-US' : 'en-US', {
    year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC',
  }).format(d);
}

/** The toggle's own labels. Language names, in their own language. */
const LANG_NAMES: Record<Lang, string> = { en: 'English', es: 'Español' };

function money(rate: number): string {
  return `$${rate.toFixed(2)}`;
}

/**
 * Splits "Heading\n\nBody…" into its heading and the rest.
 *
 * ⚠️ IT NEVER DROPS ANYTHING. Whatever is not the first paragraph is returned
 * whole, newlines intact, and rendered with `white-space: pre-wrap` — so the
 * numbered items, the lettered sub-items and their indentation survive without
 * this file parsing the document's structure. A parser that understood the
 * list format could silently lose an item; this cannot.
 *
 * The four blocks it is used on are all formatted that way in the source
 * document, and the API's copy spec asserts it, so a document that changes
 * shape fails a test rather than rendering a paragraph as a heading.
 */
function headed(text: string): { heading: string; body: string } {
  const at = text.indexOf('\n\n');
  if (at === -1) return { heading: '', body: text };
  return { heading: text.slice(0, at).trim(), body: text.slice(at + 2) };
}

/**
 * ⚠️ EVERY CLASS THIS PAGE USES IS DECLARED RIGHT HERE, and that is a rule
 * rather than a habit.
 *
 * `apps/web` has no CSS Modules and no shadow DOM — a component's `<style>`
 * block is an ordinary global stylesheet, live only while that component is
 * mounted. `cssCoPresence.spec.ts` normally catches a borrowed class by
 * walking the route tree from `App.tsx`, but it assumes every route renders
 * inside the app shell, and this one deliberately does not: no Header, no
 * Navigation, no HelpDrawer. A class borrowed from any of them would look
 * available to that audit and render as bare OS chrome here.
 *
 * So this block is self-sufficient, the spec beside this file asserts it, and
 * the page uses no `ew-` utility either — tokens.css is a `<link>` in
 * index.html and so genuinely always loaded, but pinning the rule at "nothing
 * outside this file" leaves no judgement call for the next person.
 */
const css = `
  .ta-page {
    min-height: 100vh;
    background: var(--ew-n-50, #F7F7F7);
    color: var(--ew-dark, #1A1A1A);
    font-family: var(--ew-font, system-ui, -apple-system, 'Segoe UI', sans-serif);
    padding: 16px 16px 64px;
  }
  .ta-sheet {
    max-width: 680px;
    margin: 0 auto;
    background: #fff;
    border: 1px solid var(--ew-n-200, #E3E3E3);
    border-radius: 12px;
    overflow: hidden;
  }

  .ta-langbar { display: flex; justify-content: flex-end; gap: 6px; max-width: 680px; margin: 0 auto 10px; }
  .ta-lang {
    background: #fff; border: 1px solid var(--ew-n-200, #E3E3E3);
    border-radius: 999px; padding: 7px 14px; min-height: 36px;
    font: 600 13px inherit; color: var(--ew-n-500, #6B6B6B); cursor: pointer;
  }
  .ta-lang-on { background: var(--ew-dark, #1A1A1A); border-color: var(--ew-dark, #1A1A1A); color: #fff; }
  /* Signed: the buttons are disabled, and now LOOK it — a control that looks
     live and does nothing reads as broken. The signed language keeps its full
     weight, since it is the record of which version was read; only the other
     one fades. */
  .ta-lang[disabled] { cursor: default; }
  .ta-lang[disabled]:not(.ta-lang-on) { opacity: 0.4; }

  .ta-hd { padding: 20px 24px; border-bottom: 1px solid var(--ew-n-100, #EFEFEF); }
  .ta-title { font-size: 20px; font-weight: 600; line-height: 1.25; margin: 0 0 8px; }
  .ta-job { font-size: 15px; font-weight: 500; }
  .ta-meta { font-size: 13px; color: var(--ew-n-500, #6B6B6B); margin-top: 4px; }
  .ta-for { font-size: 13px; color: var(--ew-n-500, #6B6B6B); margin-top: 8px; }
  .ta-for b { color: var(--ew-dark, #1A1A1A); font-weight: 600; }

  .ta-sec { padding: 20px 24px; border-bottom: 1px solid var(--ew-n-100, #EFEFEF); }
  .ta-sec:last-child { border-bottom: none; }
  .ta-h { font-size: 16px; font-weight: 600; line-height: 1.3; margin: 0 0 10px; }

  /* ⚠️ pre-wrap, not pre-line: the general terms indent their sub-items with
     spaces, and pre-line collapses them. */
  .ta-prose { font-size: 14.5px; line-height: 1.6; white-space: pre-wrap; margin: 0; }
  .ta-prose-sm { font-size: 13.5px; line-height: 1.6; white-space: pre-wrap; margin: 0; color: var(--ew-n-700, #3D3D3D); }
  .ta-prose-sm mark { background: var(--ew-safety-50, #FFF8E1); color: inherit; box-shadow: 0 0 0 3px var(--ew-safety-50, #FFF8E1); border-radius: 2px; }

  .ta-lines { list-style: none; margin: 12px 0 0; padding: 0; }
  .ta-line {
    display: flex; justify-content: space-between; align-items: baseline; gap: 16px;
    padding: 11px 0; border-bottom: 1px solid var(--ew-n-100, #EFEFEF);
  }
  .ta-line:first-child { border-top: 1px solid var(--ew-n-100, #EFEFEF); }
  .ta-line-label { font-size: 15px; }
  .ta-line-rate {
    font-size: 15px; font-weight: 600; white-space: nowrap; text-align: right;
    font-variant-numeric: tabular-nums;
  }
  .ta-line-unit { font-weight: 400; color: var(--ew-n-500, #6B6B6B); margin-left: 4px; }

  .ta-label { display: block; font-size: 13px; font-weight: 600; margin: 16px 0 6px; }
  .ta-input {
    width: 100%; padding: 12px; font-size: 16px; font-family: inherit;
    border: 1px solid var(--ew-n-300, #CFCFCF); border-radius: 8px; background: #fff;
    color: inherit;
  }
  .ta-input:focus { outline: 2px solid var(--ew-green, #2E7D32); outline-offset: 1px; }
  .ta-hint { font-size: 13px; line-height: 1.5; color: var(--ew-n-500, #6B6B6B); margin: 8px 0 0; }

  .ta-check { display: flex; gap: 10px; align-items: flex-start; margin-top: 18px; cursor: pointer; }
  .ta-check input { width: 22px; height: 22px; margin: 0; flex: 0 0 auto; accent-color: var(--ew-green, #2E7D32); }
  .ta-check span { font-size: 14.5px; line-height: 1.45; }

  .ta-btn {
    margin-top: 18px; width: 100%; min-height: 48px; padding: 12px 20px;
    font-size: 16px; font-weight: 600; font-family: inherit;
    border: none; border-radius: 8px; cursor: pointer;
    background: var(--ew-green, #2E7D32); color: #fff;
  }
  .ta-btn[disabled] { opacity: 0.5; cursor: default; }

  .ta-done {
    margin: 0; padding: 14px 16px; border-radius: 8px;
    background: var(--ew-green-50, #F0FDF4);
    border: 1px solid var(--ew-green, #2E7D32);
    font-size: 15px; line-height: 1.5;
  }
  .ta-error {
    margin: 14px 0 0; padding: 12px 14px; border-radius: 8px;
    background: #FEF2F2; border: 1px solid var(--ew-danger, #C62828);
    font-size: 14px; line-height: 1.5;
  }
  .ta-state { max-width: 680px; margin: 0 auto; padding: 48px 8px; font-size: 15px; text-align: center; }
`;

export default function TravelAcknowledgmentPage({ token }: { token: string }) {
  const [ack, setAck] = useState<Acknowledgment | null>(null);
  const [loadError, setLoadError] = useState<'notFound' | 'failed' | null>(null);
  const [lang, setLang] = useState<Lang>('en');
  const [name, setName] = useState('');
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [signError, setSignError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      /**
       * ⚠️ THE TOKEN GOES IN THE BODY, NEVER THE URL. This was
       * `GET /travel-acknowledgment/<token>`, and in production the API's
       * request telemetry records every URL — so each open wrote a live
       * credential into the logs, where anybody with access could sign an
       * unsigned link as somebody else. (Codex, P1 on #542.) A POST body is not
       * recorded. See `AcknowledgmentTokenDto` on the API for why redacting it
       * in telemetry instead was not available.
       */
      const res = await fetch(`${API_ROOT}/travel-acknowledgment/lookup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      if (!res.ok) {
        setLoadError(res.status === 404 ? 'notFound' : 'failed');
        return;
      }
      const body: Acknowledgment = await res.json();
      setAck(body);
      // A signed link reopens in the language it was signed in — that is the
      // version this person read, and the one the record attaches to.
      if (body.signedLanguage === 'es' || body.signedLanguage === 'en') setLang(body.signedLanguage);
    } catch {
      setLoadError('failed');
    }
  }, [token]);

  useEffect(() => { void load(); }, [load]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setSignError(null);
    setSubmitting(true);
    try {
      const res = await fetch(
        // In the body for the same reason as the lookup above.
        `${API_ROOT}/travel-acknowledgment/sign`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            token,
            signedName: name.trim(),
            acknowledgedTerms: checked,
            // What they read, not what they prefer.
            language: lang,
          }),
        },
      );
      if (!res.ok) {
        /**
         * ⚠️ The API's own message rather than a generic one. A conflict here
         * says who signed and when, which is the answer somebody re-opening an
         * old link is actually looking for.
         */
        const body = await res.json().catch(() => null);
        setSignError(
          (typeof body?.message === 'string' && body.message) || FALLBACK[lang].failed,
        );
        // Re-read, so a form signed elsewhere settles into that state rather
        // than sitting behind an error the reader cannot clear.
        await load();
        return;
      }
      await load();
    } catch {
      setSignError(FALLBACK[lang].failed);
    } finally {
      setSubmitting(false);
    }
  }

  if (loadError) return <PageState kind={loadError} />;

  if (!ack) return <PageState kind="loading" />;

  /**
   * ⚠️ NO POLICY MEANS NO FORM. A row issued before the terms were frozen has
   * amounts and nothing to acknowledge. Showing the amounts alone and a Sign
   * button would ask somebody to agree to terms that were never on the page —
   * so the page says it cannot be shown, and the scheduler issues a new link.
   */
  if (!ack.policy) return <PageState kind="notFound" />;

  return (
    <AcknowledgmentSheet
      ack={ack}
      // A signed form shows the language it was signed in, whatever `lang`
      // says — see `lockedLanguage`. Chosen here, where the text is picked,
      // not only by disabling the buttons.
      policy={ack.policy[lockedLanguage(ack) ?? lang]}
      lang={lockedLanguage(ack) ?? lang}
      onLang={setLang}
      name={name}
      onName={setName}
      checked={checked}
      onChecked={setChecked}
      onSubmit={submit}
      submitting={submitting}
      signError={signError}
    />
  );
}

/**
 * The general term that governs a prevailing-wage job — item 6 in both
 * languages of the approved text. `travel-acknowledgment-copy.spec.ts` on the
 * API pins that item 6 IS that term, so a renumbered handbook fails there
 * rather than this page quietly highlighting whichever term became 6.
 */
export const PREVAILING_WAGE_TERM = 6;

/**
 * The general terms, split so that ONE numbered item can be highlighted —
 * without changing, adding or dropping a single character.
 *
 * ⚠️ WHY IT EXISTS. On a prevailing-wage job the approved tier text lists the
 * meals per diem and the hourly increase unconditionally, while the amounts
 * beside it — correctly — do not. The handbook's own answer is general term 6,
 * sixteen items further down. The product owner's ruling (2026-09-23): keep
 * every word the same, and highlight term 6 so the reader sees it applies.
 * Emphasis only. No sentence is added, which is what keeps the form from
 * deviating from the handbook.
 *
 * ⚠️ THE SEGMENTS CONCATENATE BACK TO THE INPUT, EXACTLY. This text is what a
 * person signs, so a split that lost or duplicated a character would change
 * the document to decorate it. When the item is absent, or no item is asked
 * for, the whole text comes back as one plain segment — never a partial one.
 *
 * An item is a paragraph (separated by a blank line) that begins with its
 * number and a full stop, so asking for 6 can never match "16." — that
 * paragraph begins with "1".
 */
export function termSegments(text: string, item: number | null): { text: string; mark: boolean }[] {
  if (item == null) return [{ text, mark: false }];
  const match = new RegExp(`(^|\\n\\n)(${item}\\. [\\s\\S]*?)(?=\\n\\n|$)`).exec(text);
  if (!match) return [{ text, mark: false }];
  const start = match.index + match[1].length;
  const end = start + match[2].length;
  return [
    { text: text.slice(0, start), mark: false },
    { text: text.slice(start, end), mark: true },
    { text: text.slice(end), mark: false },
  ].filter((s) => s.text !== '');
}

/**
 * The language a SIGNED form is locked to, or null when the reader may choose.
 *
 * ⚠️ A SIGNATURE ATTACHES TO THE TEXT THAT WAS READ, NOT TO BOTH. After a
 * Spanish acknowledgment was signed, reopening it selected Spanish — but the
 * toggle stayed live, and switching rendered the English policy directly above
 * the "Signed by" block: a signature displayed as attached to text the signer
 * never read, on the one record `signed_language` exists to keep honest.
 * (Codex, P2 on #542.)
 *
 * Null when unsigned, and also when signed with no recorded language — a row
 * signed before the column existed. There is then no fact to lock to, and
 * inventing one would be the same misstatement in the other direction.
 *
 * One function, used by the page to pick the text AND by the form to disable
 * the buttons, so the two can never disagree about whether the form is locked.
 */
export function lockedLanguage(ack: Pick<Acknowledgment, 'signedAt' | 'signedLanguage'>): Lang | null {
  if (ack.signedAt == null) return null;
  return ack.signedLanguage === 'en' || ack.signedLanguage === 'es' ? ack.signedLanguage : null;
}

/**
 * Every state that is not the form — loading, not found, failed — in BOTH
 * languages at once.
 *
 * ⚠️ BOTH, BECAUSE THE PAGE CANNOT KNOW WHICH YET. The language toggle lives
 * on the form, and these states render before there is a form: a link that
 * 404s never gets one. So with the page starting in English, the Spanish "this
 * link is not valid" was unreachable for the one case it was written for — a
 * Spanish-speaking recipient with a link mangled in transit saw English only.
 * (Codex, P2 on #542.)
 *
 * Both languages over a toggle, deliberately: a toggle is a control to find
 * and press on a screen that says one sentence, and guessing from the browser
 * would change the FORM's starting language too, which is a product decision
 * rather than a fix. Showing both needs nothing from the reader and adds no
 * words — these are the page's existing strings.
 *
 * Each paragraph carries its own `lang`, so a screen reader switches
 * pronunciation between them.
 */
export function PageState({ kind }: { kind: 'loading' | 'notFound' | 'failed' }) {
  return (
    <>
      <style>{css}</style>
      <div className="ta-page" lang="en">
        <p className="ta-state" lang="en">{FALLBACK.en[kind]}</p>
        <p className="ta-state" lang="es">{FALLBACK.es[kind]}</p>
      </div>
    </>
  );
}

/**
 * The form itself, with no fetching in it.
 *
 * ⚠️ SPLIT OUT SO IT CAN BE TESTED. `apps/web` runs vitest in a node
 * environment with no jsdom, so specs render to static markup — and a static
 * render never runs an effect, which means the page above is permanently on
 * "Loading…" under test. Everything worth asserting here is about what the
 * form SAYS, so the copy needs a component that can be handed a record.
 */
export function AcknowledgmentSheet({
  ack, policy, lang, onLang, name, onName, checked, onChecked, onSubmit, submitting, signError,
}: {
  ack: Acknowledgment;
  policy: Policy;
  lang: Lang;
  onLang: (l: Lang) => void;
  name: string;
  onName: (v: string) => void;
  checked: boolean;
  onChecked: (v: boolean) => void;
  onSubmit: (e: React.FormEvent) => void;
  submitting: boolean;
  signError: string | null;
}) {
  const { job, person } = ack;
  const signed = ack.signedAt != null;
  const locked = lockedLanguage(ack) != null;
  const benefits = headed(policy.benefitsHeading);
  const tier = headed(policy.tierPolicy);
  const terms = headed(policy.generalTerms);
  const ackStatement = headed(policy.acknowledgment);

  return (
    <>
      <style>{css}</style>
      {/*
        ⚠️ `lang` IS AN HTML ATTRIBUTE HERE, and it was missing. The page used
        to pass `lang` only as a React prop, so the rendered markup inherited
        index.html's `<html lang="en">` — and a screen reader read the Spanish
        acknowledgment, the legal text somebody is signing, with English
        pronunciation rules. (Codex, P2 on #542.) The states that are not the
        form — loading, not found, failed — are in `PageState`, which shows
        both languages and marks each paragraph separately.
      */}
      <div className="ta-page" lang={lang}>
        <div className="ta-langbar">
          {(['en', 'es'] as const).map((l) => (
            <button
              key={l}
              type="button"
              className={l === lang ? 'ta-lang ta-lang-on' : 'ta-lang'}
              onClick={() => { if (!locked) onLang(l); }}
              aria-pressed={l === lang}
              // Signed: the version read is the version shown. See `lockedLanguage`.
              disabled={locked}
              // Each label is written in its OWN language ("Español" on the
              // English page), so each is marked as such — otherwise the one
              // word a Spanish reader is looking for is mispronounced.
              lang={l}
            >
              {LANG_NAMES[l]}
            </button>
          ))}
        </div>

        <div className="ta-sheet">
          <div className="ta-hd">
            <h1 className="ta-title">{policy.formTitle}</h1>
            <div className="ta-job">
              {job.jobNumber ? `${policy.labels.jobNumber} ${job.jobNumber}` : policy.labels.projectName}
              {job.title ? ` — ${job.title}` : ''}
            </div>
            <div className="ta-meta">
              {/* Shown only when it differs: a maintenance sub-project carries
                  an `MNT-` number of its own, and the five-digit one everybody
                  uses in conversation belongs to the parent. */}
              {job.parentJobNumber && job.parentJobNumber !== job.jobNumber
                && <>{policy.labels.jobNumber} {job.parentJobNumber} · </>}
              {job.travelBand ?? '—'}
            </div>
            {person.name && (
              <div className="ta-for">
                {policy.labels.employeeName}: <b>{person.name}</b>
              </div>
            )}
            {/* The approved form's other pre-filled fields. Each line needs
                BOTH its label and its value from the frozen record: a link
                issued before these existed has neither, and the page shows
                nothing rather than a label with a blank, or a value under a
                label this file would have to author. */}
            {policy.labels.siteAddress && job.siteAddress && (
              <div className="ta-for">
                {policy.labels.siteAddress}: <b>{job.siteAddress}</b>
              </div>
            )}
            {policy.labels.regionalOffice && person.regionalOffice && (
              <div className="ta-for">
                {policy.labels.regionalOffice}: <b>{person.regionalOffice}</b>
              </div>
            )}
            {policy.labels.mobilizationDate && job.mobilizationDate && (
              <div className="ta-for">
                {policy.labels.mobilizationDate}: <b>{formatFormDate(job.mobilizationDate, lang)}</b>
              </div>
            )}
          </div>

          <div className="ta-sec">
            <p className="ta-prose">{policy.welcome}</p>
          </div>

          {/*
            The amounts. ⚠️ Labelled from `policy.amountLabels` by `kind`, NOT
            from `line.label` — the engine's labels exist only in English, and a
            Spanish reader would otherwise get approved Spanish policy above a
            column of English money labels. The engine's label stays in the
            frozen record either way.
          */}
          <div className="ta-sec">
            {benefits.heading && <h2 className="ta-h">{benefits.heading}</h2>}
            <p className="ta-prose">{benefits.body}</p>
            {ack.lines.length > 0 && (
              <ul className="ta-lines">
                {ack.lines.map((l) => (
                  <li className="ta-line" key={l.kind}>
                    <span className="ta-line-label">{policy.amountLabels[l.kind] ?? l.label}</span>
                    <span className="ta-line-rate">
                      {money(l.rate)}
                      <span className="ta-line-unit">{policy.unitLabels[l.unit]}</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="ta-sec">
            {tier.heading && <h2 className="ta-h">{tier.heading}</h2>}
            <p className="ta-prose">{tier.body}</p>
          </div>

          <div className="ta-sec">
            {terms.heading && <h2 className="ta-h">{terms.heading}</h2>}
            {/* Term 6 highlighted on a prevailing-wage job, and nothing else
                changed — see `termSegments`. `<mark>` rather than a styled
                span, because "highlighted for relevance" is what it means. */}
            <p className="ta-prose-sm">
              {termSegments(terms.body, job.isPrevailingWage === true ? PREVAILING_WAGE_TERM : null)
                .map((s, i) => (s.mark ? <mark key={i}>{s.text}</mark> : <span key={i}>{s.text}</span>))}
            </p>
          </div>

          <div className="ta-sec">
            {ackStatement.heading && <h2 className="ta-h">{ackStatement.heading}</h2>}
            {signed ? (
              <p className="ta-done">
                {policy.signedPrefix} {ack.signedName} · {new Date(ack.signedAt!).toLocaleDateString(
                  lang === 'es' ? 'es-US' : 'en-US',
                  { year: 'numeric', month: 'long', day: 'numeric' },
                )}
              </p>
            ) : (
              <form onSubmit={onSubmit}>
                <p className="ta-prose">{ackStatement.body}</p>

                <label className="ta-label" htmlFor="ta-name">
                  {policy.labels.typeYourFullName}
                </label>
                <input
                  id="ta-name"
                  className="ta-input"
                  type="text"
                  value={name}
                  onChange={(e) => onName(e.target.value)}
                  autoComplete="name"
                  maxLength={200}
                  required
                />
                <p className="ta-hint">{policy.signatureHint}</p>

                {/* ⚠️ Required, because the statement above says they checked
                    it. The API refuses a submission without it rather than
                    storing a signature the statement misdescribes. */}
                <label className="ta-check">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => onChecked(e.target.checked)}
                  />
                  <span>{policy.checkboxLabel}</span>
                </label>

                <button
                  className="ta-btn"
                  type="submit"
                  disabled={submitting || !checked || name.trim().length < 2}
                >
                  {policy.submitLabel}
                </button>
                {signError && <p className="ta-error">{signError}</p>}
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
